import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import ContactMap from './ContactMap';

function gridToLatLon(grid) {
  if (!grid || grid.length < 4) return null;
  grid = grid.toUpperCase();
  
  const lon = (grid.charCodeAt(0) - 65) * 20 - 180;
  const lat = (grid.charCodeAt(1) - 65) * 10 - 90;
  const lonSub = parseInt(grid[2]) * 2;
  const latSub = parseInt(grid[3]);
  
  let finalLon = lon + lonSub + 1;
  let finalLat = lat + latSub + 0.5;
  
  if (grid.length >= 6) {
    const lonSubSub = (grid.charCodeAt(4) - 65) * (2 / 24);
    const latSubSub = (grid.charCodeAt(5) - 65) * (1 / 24);
    finalLon = lon + lonSub + lonSubSub + (1 / 24);
    finalLat = lat + latSub + latSubSub + (0.5 / 24);
  }
  
  return { lat: finalLat, lon: finalLon };
}

function MapView({ token, socket, apiUrl }) {
  const [contacts, setContacts] = useState([]);
  const [sectionCounts, setSectionCounts] = useState({});
  const [settings, setSettings] = useState({ club_grid: '', club_callsign: '' });
  const [newContactId, setNewContactId] = useState(null);

  const fetchContacts = useCallback(async () => {
    try {
      const response = await axios.get(`${apiUrl}/api/contacts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContacts(response.data);
    } catch (err) {
      console.error('Error fetching contacts:', err);
    }
  }, [apiUrl, token]);

  const fetchSectionCounts = useCallback(async () => {
    try {
      const response = await axios.get(`${apiUrl}/api/sections`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSectionCounts(response.data);
    } catch (err) {
      console.error('Error fetching section counts:', err);
    }
  }, [apiUrl, token]);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${apiUrl}/api/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
    } catch (err) {
      console.error('Error fetching settings:', err);
    }
  }, [apiUrl, token]);

  useEffect(() => {
    fetchContacts();
    fetchSectionCounts();
    fetchSettings();
  }, [fetchContacts, fetchSectionCounts, fetchSettings]);

  useEffect(() => {
    if (socket) {
      const handleNewContact = (contact) => {
        setContacts(prev => [contact, ...prev]);
        setNewContactId(contact.id);
        setTimeout(() => setNewContactId(null), 1000);
        fetchSectionCounts();
      };

      const handleContactUpdated = (contact) => {
        setContacts(prev => prev.map(c => c.id === contact.id ? contact : c));
        fetchSectionCounts();
      };

      const handleContactDeleted = ({ id }) => {
        setContacts(prev => prev.filter(c => c.id !== id));
        fetchSectionCounts();
      };

      socket.on('newContact', handleNewContact);
      socket.on('contactUpdated', handleContactUpdated);
      socket.on('contactDeleted', handleContactDeleted);

      return () => {
        socket.off('newContact', handleNewContact);
        socket.off('contactUpdated', handleContactUpdated);
        socket.off('contactDeleted', handleContactDeleted);
      };
    }
  }, [socket, fetchSectionCounts]);

  const contactMarkers = contacts
    .filter(c => c.contact_lat && c.contact_lon)
    .map(c => ({
      callsign: c.contact_callsign,
      lat: parseFloat(c.contact_lat),
      lon: parseFloat(c.contact_lon),
      grid: c.contact_grid,
      city: c.contact_city,
      state: c.contact_state
    }));

  const latestContacts = contacts.slice(0, 10);

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  return (
    <div className="map-view">
      <div className="map-fullscreen">
        <ContactMap 
          sectionCounts={sectionCounts} 
          contactMarkers={contactMarkers}
          clubQth={settings.club_grid ? {
            ...gridToLatLon(settings.club_grid),
            grid: settings.club_grid,
            callsign: settings.club_callsign
          } : null}
        />
      </div>
      
      <div className="map-overlay">
        <h3 className="map-overlay-title">Latest Contacts</h3>
        <div className="map-overlay-list">
          {latestContacts.map(contact => (
            <div 
              key={contact.id} 
              className={`map-overlay-contact ${contact.id === newContactId ? 'new-overlay-contact' : ''}`}
            >
              <div className="overlay-row-top">
                <span className="overlay-callsign">{contact.contact_callsign}</span>
                <span className="overlay-exchange">{contact.class} {contact.section}</span>
              </div>
              <div className="overlay-row-bottom">
                <span className="overlay-freq">{parseFloat(contact.frequency).toFixed(3)} MHz</span>
                <span className="overlay-mode">{contact.mode}</span>
                <span className="overlay-time">{formatTime(contact.timestamp)}</span>
              </div>
            </div>
          ))}
          {latestContacts.length === 0 && (
            <div className="map-overlay-empty">No contacts yet</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default MapView;
